import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    type ElementRef,
    input,
    output,
    viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgIcon } from '@ng-icons/core';
import {
    QpxButton,
    QpxIcon,
    QpxPill,
    QpxTooltip,
    QpxTypography,
} from '@quipex/shared/ui';
import type { QuippyPackPreview as Preview } from '../../../../runtime/pack-actions/models';
import { QuippyPackAssignedDocument } from '../assigned-document/assigned-document';
import { QuippyPackReviewActions } from './review-actions/review-actions';
@Component({
    selector: 'qpx-quippy-pack-preview',
    imports: [
        MatButtonModule,
        QpxButton,
        QuippyPackReviewActions,
        QuippyPackAssignedDocument,
        MatProgressSpinnerModule,
        NgIcon,
        QpxIcon,
        QpxPill,
        QpxTooltip,
        QpxTypography,
    ],
    templateUrl: './pack-preview.html',
    styleUrl: './pack-preview.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuippyPackPreview {
    public readonly preview = input.required<Preview>();
    protected readonly covered = computed(
        () =>
            this.preview().rows.filter((row) => row.documents.length > 0).length
    );
    protected readonly progress = computed(() =>
        Math.round((100 * this.covered()) / this.preview().rows.length)
    );
    protected readonly status = computed(() => {
        if (this.progress() === 100)
            return {
                label: 'Complete',
                context: 'success',
                tone: 'muted',
            } as const;
        if (this.progress() === 0)
            return {
                label: 'Not started',
                context: 'secondary',
                tone: 'muted',
            } as const;
        return {
            label: 'In progress',
            context: 'primary',
            tone: 'muted',
        } as const;
    });
    protected readonly groups = computed(() => {
        let offset = 0;
        return this.preview().command.sections.map((section) => {
            const rows = this.preview().rows.slice(
                offset,
                (offset += section.requirements.length)
            );
            const covered = rows.filter(
                (row) => row.documents.length > 0
            ).length;
            const coverage =
                rows.length === 0
                    ? ({
                          icon: 'lucideMinus',
                          context: 'secondary',
                          label: 'No requirements',
                      } as const)
                    : covered === rows.length
                      ? ({
                            icon: 'lucideCircleCheckBig',
                            context: 'success',
                            label: 'All requirements covered',
                        } as const)
                      : ({
                            icon: 'lucideCircleAlert',
                            context: 'destructive',
                            label: `${rows.length - covered} ${rows.length - covered === 1 ? 'requirement' : 'requirements'} missing`,
                        } as const);
            return {
                coverage,
                name: section.name,
                rows,
                covered,
            };
        });
    });
    public readonly editClicked = output<void>();
    public readonly applyClicked = output<void>();
    private readonly reviewHeading =
        viewChild<ElementRef<HTMLElement>>('reviewHeading');
    constructor() {
        effect(() => this.reviewHeading()?.nativeElement.focus());
    }
}
